import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { IndividualProjectsOverviewStyles as styles } from './individual-projects-overview.style';
import { IndividualProjectsOverviewTemplate as template } from './individual-projects-overview.template';
import { IProjectService, IVppaService } from '../../../../services';
import { IProjectDocumentStatus, IProjectVPPA, WatchlistProject } from '../../../../types';
import {
  PROFILE_RIGHTS,
  PROJECT_OPERATIONAL_STATUS,
  TECHNOLOGY,
  VPPA_RFQ_STATUS,
} from '../../../../utils';
import {
  formatDateLong,
  getFillColorFromProjectStatus,
  getFormattedNumber,
} from '../../../../utils/formatting';
import { CASCADE_ENUM, IEnumService } from '../../../../services/enum.service';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../../utils/dataserver';
import { IUserService } from '../../../../services/user.service';
import { SellerTileValue } from '../../seller-dashboard-components/seller-tile/seller-tile';
import { getTechnologyIconName } from '../../../../styles';

enum RowObjectButtonEnum {
  NOT_BUTTON = 'NOT_BUTTON',
  GREEN_BUTTON = 'GREEN_BUTTON',
  YELLOW_BUTTON = 'YELLOW_BUTTON',
  RED_BUTTON = 'RED_BUTTON',
}

interface IRowObject {
  title: string;
  value: string;
  buttonType: RowObjectButtonEnum;
}

const name = 'individual-projects-overview';

@customElement({
  name,
  template,
  styles,
})
export class IndividualProjectsOverview extends FASTElement {
  @IProjectService projectService: IProjectService;
  @IVppaService vppaService: IVppaService;
  @IEnumService enumService: IEnumService;
  @IUserService userService: IUserService;

  @attr @observable project: IProjectDocumentStatus;
  @observable tileList: Array<SellerTileValue> = [];
  @observable generalInformation: Array<IRowObject> = [];
  @observable contractsAndReports: Array<IRowObject> = [];
  @observable permitsGovernmentApprovals: Array<IRowObject> = [];
  @observable vppaList: Array<any> = [];
  @observable totalContracts: string = '';
  @observable iProjectVPPA: IProjectVPPA;
  @observable vppaSubscription: SocketSubscription;
  @observable projectWatchlistSubscription: SocketSubscription;

  @observable showUncheckedWatchlist: boolean = false;
  @observable showCheckedWatchlist: boolean = false;
  @observable watchlist: Array<WatchlistProject> = [];

  connectedCallback() {
    super.connectedCallback();
  }

  public updateProjectWatchList(isFavorite: boolean) {
    if (isFavorite) {
      this.projectService.eventInsertFavoritedProject(this.project.PROJECT_ID);
    } else {
      this.projectService.eventDeleteFavoritedProject(this.project.PROJECT_ID);
    }
  }

  private getWatchlistButtonStatus() {
    if (!this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.BUYER_VISIBILITY)) {
      return;
    }

    let resultList = [];

    this.projectWatchlistSubscription = this.projectService
      .getAllWatchlistProjects(
        "WATCHLIST_ENTITY_ID == '" +
          this.project.PROJECT_ID +
          "' && WATCHLIST_ENTITY_ID_TYPE == 'PROJECT'",
      )
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.watchlist = updateArray(resultList, this.watchlist);
            if (this.watchlist.length > 0) {
              this.showCheckedWatchlist = true;
              this.showUncheckedWatchlist = false;
            } else {
              this.showCheckedWatchlist = false;
              this.showUncheckedWatchlist = true;
            }
          } else {
            this.showCheckedWatchlist = false;
            this.showUncheckedWatchlist = true;
          }
        },
        error: (error: any) => {},
      });
  }

  private getLocationString(stateAbreviation: string, countyName?: string) {
    if (countyName && countyName !== '') {
      return `${countyName}, ${stateAbreviation}`;
    }
    return `${stateAbreviation}`;
  }

  setProject(project: IProjectDocumentStatus) {
    if (project) {
      this.project = project;
      this.getWatchlistButtonStatus();
      this.getListedContract();
      this.loadGeneralInformation();
      this.loadContractsAndReports();
      this.loadPermitsGovernmentApprovals();
    }
  }

  updateProject(project: IProjectDocumentStatus) {
    if (project) {
      this.project = project;
      this.tileList.splice(0);
      this.generalInformation.splice(0);
      this.contractsAndReports.splice(0);
      this.permitsGovernmentApprovals.splice(0);
      this.setProject(project);
    }
  }

  private getListedContract() {
    let resultList = [];
    this.vppaSubscription = this.vppaService
      .getProjectVppas(
        `PROJECT_ID == '` +
          this.project.PROJECT_ID +
          `' && ( VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}'|| VPPA_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.TRADE_PENDING}')`,
      )
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.vppaList = updateArray(resultList, this.vppaList);
          }
          if (this.tileList.length === 0) {
            this.loadTileList();
          }
        },
        error: (error: any) => {},
      });
  }

  private async loadTileList() {
    const isNewBuild =
      this.project.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.NEW_BUILD;
    this.tileList.push({
      title: 'Project Status',
      items: [
        {
          value: isNewBuild ? 'New-Build' : 'Operational',
          icon: null,
        },
      ],
    });
    this.tileList.push({
      title: 'Technology Type',
      items: [
        {
          value: this.project.TECHNOLOGY,
          icon: {
            name: getTechnologyIconName(this.project.TECHNOLOGY),
            slot: 'start',
            fill: getFillColorFromProjectStatus(this.project.PROJECT_OPERATIONAL_STATUS),
          },
        },
      ],
    });
    this.tileList.push({
      title: 'ISO',
      items: [
        {
          value: this.project.ISO,
          icon: null,
        },
      ],
    });
    this.tileList.push({
      title: 'Nameplate Capacity',
      items: [
        {
          value: getFormattedNumber(this.project.MEGAWATT_AC) + 'MW',
          icon: null,
        },
      ],
    });

    this.tileList.push({
      title: isNewBuild ? 'Target COD' : 'COD',
      items: [
        {
          value: formatDateLong(isNewBuild ? this.project.TARGET_COD : this.project.COD),
          icon: null,
        },
      ],
    });
    this.tileList.push({
      title: 'Location',
      items: [
        {
          value: this.getLocationString(this.project.STATE_ABBREVIATION, this.project.COUNTY_NAME),
          icon: null,
        },
      ],
    });
    this.tileList.push({
      title: 'Total Offered Contracts',
      items: [
        {
          value: this.vppaList.length.toString(), // Number of CPPAs that are linked to the project with the vppa status = Active
          icon: null,
        },
      ],
    });
    if (isNewBuild) {
      let projectScore = 0;
      await this.projectService.getProjectScore(this.project.PROJECT_ID).then((response) => {
        if (response && response.REPLY.length > 0) {
          projectScore = response.REPLY[0].PROJECT_SCORE;
        }
      });

      this.tileList.push({
        title: 'CleanTrade Project Score',
        items: [
          {
            value: projectScore + '/100', // not available yet
            icon: null,
          },
        ],
      });
    }
  }

  private loadGeneralInformation() {
    this.generalInformation.push({
      title: 'Operator',
      value: this.project.PROJECT_OPERATOR,
      buttonType: RowObjectButtonEnum.NOT_BUTTON,
    });
    this.generalInformation.push({
      title: 'Transacting Entity',
      value: this.project.CASCADE_COMPANY_NAME,
      buttonType: RowObjectButtonEnum.NOT_BUTTON,
    });
    this.generalInformation.push({
      title: 'P50 Capacity Factor',
      value: this.project.P50_CAPACITY_FACTOR
        ? this.project.P50_CAPACITY_FACTOR.toString() + '%'
        : '',
      buttonType: RowObjectButtonEnum.NOT_BUTTON,
    });
    if (this.project.TECHNOLOGY === TECHNOLOGY.SOLAR) {
      this.generalInformation.push({
        title: 'Panel Make & Model',
        value: this.project.PANEL_MAKE_AND_MODEL,
        buttonType: RowObjectButtonEnum.NOT_BUTTON,
      });
      this.generalInformation.push({
        title: 'Panel Type',
        value: this.enumService.getEnumTranslation(
          CASCADE_ENUM.PANEL_TYPE,
          this.project.PANEL_TYPE,
        ),
        buttonType: RowObjectButtonEnum.NOT_BUTTON,
      });
      this.generalInformation.push({
        title: 'Tracking Type',
        value: this.enumService.getEnumTranslation(
          CASCADE_ENUM.TRACKING_TYPE,
          this.project.TRACKING_TYPE,
        ),
        buttonType: RowObjectButtonEnum.NOT_BUTTON,
      });
    } else {
      this.generalInformation.push({
        title: 'Turbine Make & Model',
        value: this.project.TURBINE_MAKE_AND_MODEL,
        buttonType: RowObjectButtonEnum.NOT_BUTTON,
      });
      this.generalInformation.push({
        title: 'Turbine Count',
        value: this.project.TURBINE_COUNT?.toString(),
        buttonType: RowObjectButtonEnum.NOT_BUTTON,
      });
      this.generalInformation.push({
        title: 'Hub Height (meters)',
        value: this.project.HUB_HEIGHT?.toString(),
        buttonType: RowObjectButtonEnum.NOT_BUTTON,
      });
    }
    this.generalInformation.push({
      title: 'Interconnection Point',
      value: this.project.INTERCONNECTION_POINT,
      buttonType: RowObjectButtonEnum.NOT_BUTTON,
    });
    this.generalInformation.push({
      title: 'Voltage',
      value: this.project.INTERCONNECTION_VOLTAGE,
      buttonType: RowObjectButtonEnum.NOT_BUTTON,
    });
    this.generalInformation.push({
      title: 'Interconnection Queue ID',
      value: this.project.INTERCONNECTION_QUEUE_ID,
      buttonType: RowObjectButtonEnum.NOT_BUTTON,
    });
  }

  private loadContractsAndReports() {
    this.contractsAndReports.push({
      title: 'Land Control Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.LAND_CONTROL_AGREEMENTS,
      ),
      buttonType: this.getButtonColor(this.project.LAND_CONTROL_AGREEMENTS),
    });
    this.contractsAndReports.push({
      title: 'Generation Equipment Supply Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.EQUIPMENT_SUPPLY_AGREEMENTS,
      ),
      buttonType: this.getButtonColor(this.project.EQUIPMENT_SUPPLY_AGREEMENTS),
    });
    this.contractsAndReports.push({
      title: 'EPC Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.EPC_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.EQUIPMENT_SUPPLY_AGREEMENTS),
    });
    this.contractsAndReports.push({
      title: 'BoP Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.BOP_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.BOP_AGREEMENT),
    });
    this.contractsAndReports.push({
      title: 'Construction Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.CONSTRUCTION_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.CONSTRUCTION_AGREEMENT),
    });
    this.contractsAndReports.push({
      title: 'O&M Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.OPERATIONS_MAINTENANCE_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.OPERATIONS_MAINTENANCE_AGREEMENT),
    });
    this.contractsAndReports.push({
      title: 'Management Services Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.MANAGEMENT_SERVICES_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.MANAGEMENT_SERVICES_AGREEMENT),
    });
    this.contractsAndReports.push({
      title: 'Feasibility Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.FEASIBILITY_STUDY,
      ),
      buttonType: this.getButtonColor(this.project.FEASIBILITY_STUDY),
    });
    this.contractsAndReports.push({
      title: 'System Impact Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.SYSTEM_IMPACT_STUDY,
      ),
      buttonType: this.getButtonColor(this.project.SYSTEM_IMPACT_STUDY),
    });
    this.contractsAndReports.push({
      title: 'Facilities Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.FACILITIES_STUDY,
      ),
      buttonType: this.getButtonColor(this.project.FACILITIES_STUDY),
    });
    this.contractsAndReports.push({
      title: 'Interconnection Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.INTERCONNECTION_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.INTERCONNECTION_AGREEMENT),
    });
    this.contractsAndReports.push({
      title: 'Energy Management Agreement',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.ENERGY_MANAGEMENT_AGREEMENT,
      ),
      buttonType: this.getButtonColor(this.project.ENERGY_MANAGEMENT_AGREEMENT),
    });
    this.contractsAndReports.push({
      title: 'Engineering Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.INDEPENDENT_ENGINEERING_REPORT,
      ),
      buttonType: this.getButtonColor(this.project.INDEPENDENT_ENGINEERING_REPORT),
    });
    this.contractsAndReports.push({
      title: 'Energy Resource Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.ENERGY_RESOURCE_STUDY,
      ),
      buttonType: this.getButtonColor(this.project.ENERGY_RESOURCE_STUDY),
    });
    this.contractsAndReports.push({
      title: 'Transmission/Congestion Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.TRANSMISSION_CONGESTION_STUDY,
      ),
      buttonType: this.getButtonColor(this.project.TRANSMISSION_CONGESTION_STUDY),
    });
    this.contractsAndReports.push({
      title: 'Insurance Consultant Report',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.INSURANCE_CONSULTANT_REPORT,
      ),
      buttonType: this.getButtonColor(this.project.INSURANCE_CONSULTANT_REPORT),
    });
  }

  private loadPermitsGovernmentApprovals() {
    this.permitsGovernmentApprovals.push({
      title: 'Title Insurance',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.TITLE_INSURANCE,
      ),
      buttonType: this.getButtonColor(this.project.TITLE_INSURANCE),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Litigation Disclosure',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.LITIGATION_DISCLOSURE,
      ),
      buttonType: this.getButtonColor(this.project.LITIGATION_DISCLOSURE),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Certified Land Title Association Survey',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.LAND_TITLE_SURVEY,
      ),
      buttonType: this.getButtonColor(this.project.LAND_TITLE_SURVEY),
    });
    this.permitsGovernmentApprovals.push({
      title: 'FEMA Flood Hazard Determination',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.FLOOD_HAZARD_DETERMINATION,
      ),
      buttonType: this.getButtonColor(this.project.FLOOD_HAZARD_DETERMINATION),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Phase I ESA Review',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.ESA_REVIEW,
      ),
      buttonType: this.getButtonColor(this.project.ESA_REVIEW),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Wetlands Delineation Review',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.WETLANDS_DELINEATION_REVIEW,
      ),
      buttonType: this.getButtonColor(this.project.WETLANDS_DELINEATION_REVIEW),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Threatened or Endangered Species Review',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.THREATENED_ENDANGERED_SPECIES_REVIEW,
      ),
      buttonType: this.getButtonColor(this.project.THREATENED_ENDANGERED_SPECIES_REVIEW),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Cultural Resources Study',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_DRAFT_STATUS,
        this.project.CULTURAL_RESOURCES_STUDY_REVIEW,
      ),
      buttonType: this.getButtonColor(this.project.CULTURAL_RESOURCES_STUDY_REVIEW),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Primary State-Level Permit',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.PRIMARY_STATE_LEVEL_PERMIT,
      ),
      buttonType: this.getButtonColor(this.project.PRIMARY_STATE_LEVEL_PERMIT),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Primary County-Level Permit',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.PRIMARY_COUNTY_LEVEL_PERMIT,
      ),
      buttonType: this.getButtonColor(this.project.PRIMARY_COUNTY_LEVEL_PERMIT),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Primary Township-Level Permit',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.PRIMARY_TOWNSHIP_LEVEL_PERMIT,
      ),
      buttonType: this.getButtonColor(this.project.PRIMARY_TOWNSHIP_LEVEL_PERMIT),
    });
    this.permitsGovernmentApprovals.push({
      title: 'Tax Abatement Application',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.TAX_ABATEMENT_APPLICATION,
      ),
      buttonType: this.getButtonColor(this.project.TAX_ABATEMENT_APPLICATION),
    });
    this.permitsGovernmentApprovals.push({
      title: 'DNH from FAA',
      value: this.enumService.getEnumTranslation(
        CASCADE_ENUM.PROJECT_SUBMISSION_STATUS,
        this.project.DNH_FROM_FAA,
      ),
      buttonType: this.getButtonColor(this.project.DNH_FROM_FAA),
    });
  }

  private getButtonColor(value: String): RowObjectButtonEnum {
    if (value === 'EXECUTED' || value === 'APPROVED') return RowObjectButtonEnum.GREEN_BUTTON;
    if (value === 'DRAFT_AVAILABLE' || value === 'SUBMITTED')
      return RowObjectButtonEnum.YELLOW_BUTTON;
    if (value === 'WORK_IN_PROGRESS' || value === 'NOT_SUBMITTED')
      return RowObjectButtonEnum.RED_BUTTON;
    return RowObjectButtonEnum.NOT_BUTTON;
  }

  disconnectedCallback(): void {
    this.vppaSubscription?.unsubscribe();
    this.projectWatchlistSubscription?.unsubscribe();
    super.disconnectedCallback();
  }
}
